// The Product Catalog page's data shapes (apps/server/src/productPage.mjs) and
// the catalog after a move, kept apart from the fetches so they test on their own.

export type ProductStatus = 'draft' | 'active' | 'retired';

export interface CatalogProduct {
  id: string;
  code: string;
  name: string;
  structure: 'single' | 'bundle';
  status: ProductStatus;
  is_component: boolean;
}

export interface CatalogGroup {
  id: string;
  code: string;
  name: string;
  family: string;
  products: CatalogProduct[];
}

export interface CatalogFamily {
  family: string;
  groups: CatalogGroup[];
}

export interface FacetPrice {
  id: string;
  label: string | null;
  scheme: 'flat' | 'per_hour' | 'grid' | 'included';
  amount: number | null;
  period: 'month' | 'year' | null;
  is_default: boolean;
  selling_model: string;
  grid_id: string | null;
}

export interface FacetDimension {
  label: string;
  values: { key: string; label: string }[];
}

export interface FacetGrid {
  id: string;
  name: string;
  rows: FacetDimension | null;
  cols: FacetDimension | null;
  cells: { key_1: string; key_2: string | null; amount: number | null; hours: number | null }[];
}

export interface ProductFacets {
  product: { id: string; code: string; name: string; estimated_hours: number };
  prices: FacetPrice[];
  grids: FacetGrid[];
  roleSplits: { id: string; resource_role_id: string; name: string; share_pct: number | null }[];
  billing: {
    policy: { id: string; name: string } | null;
    treatments: {
      id: string;
      name: string;
      method: string;
      billing_timing: string | null;
      billing_frequency: string;
      is_default: boolean;
      items: { sequence: number; label: string; percent: number | null }[];
    }[];
  };
}

/** The catalog after a move, as the server orders it: the moved products before beforeId, or last. */
export function catalogAfterMove(catalog: CatalogFamily[], ids: string[], groupId: string, beforeId: string | null): CatalogFamily[] {
  const all = catalog.flatMap((f) => f.groups.flatMap((g) => g.products));
  const moving = ids.flatMap((id) => all.filter((p) => p.id === id));
  return catalog.map((f) => ({
    ...f,
    groups: f.groups.map((g) => {
      const kept = g.products.filter((p) => !ids.includes(p.id));
      if (g.id !== groupId) return { ...g, products: kept };
      const at = beforeId === null ? -1 : kept.findIndex((p) => p.id === beforeId);
      return { ...g, products: at < 0 ? [...kept, ...moving] : [...kept.slice(0, at), ...moving, ...kept.slice(at)] };
    }),
  }));
}
